import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';

interface WeekHeaderProps {
  weekStart: string;
  weekNumber: number;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  employeeCount?: number;
  taskCount?: number;
  conflictCount?: number;
}

export function WeekHeader({
  weekStart,
  weekNumber,
  onPreviousWeek,
  onNextWeek,
  onToday,
  employeeCount,
  taskCount,
  conflictCount,
}: WeekHeaderProps) {
  const { t } = useTranslation('planning');
  const { language } = useLanguageStore();
  const locale = language === 'de' ? 'de-DE' : language === 'sl' ? 'sl-SI' : 'en-US';

  const start = new Date(weekStart);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  const today = new Date();
  const isToday = (date: Date) => date.toDateString() === today.toDateString();

  const showStats =
    employeeCount !== undefined || taskCount !== undefined || conflictCount !== undefined;

  const statBits: { key: string; text: string; tone?: string }[] = [];
  if (employeeCount !== undefined) {
    statBits.push({
      key: 'employees',
      text: t('weekHeader.employeesVisible', { count: employeeCount }),
    });
  }
  if (taskCount !== undefined) {
    statBits.push({
      key: 'tasks',
      text: t('weekHeader.tasks', { count: taskCount }),
    });
  }
  if (conflictCount !== undefined && conflictCount > 0) {
    statBits.push({
      key: 'conflicts',
      text: t('weekHeader.conflicts', { count: conflictCount }),
      tone: 'text-status-conflict',
    });
  }

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white text-tabular shrink-0">
            {t('weekHeader.weekAbbr')} {weekNumber}
          </h2>
          <span className="text-xs sm:text-sm text-ink-muted dark:text-text-light truncate">
            {days[0].toLocaleDateString(locale, { day: '2-digit', month: 'short' })} –{' '}
            {days[6].toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onToday}>
            <Calendar className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('today')}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={onPreviousWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onNextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showStats && (
        <div className="px-3 sm:px-4 py-1.5 text-[11px] text-ink-muted dark:text-text-light flex items-center gap-2 flex-wrap border-b border-border-subtle dark:border-gray-800">
          {statBits.map((bit, i) => (
            <span key={bit.key} className="flex items-center gap-2">
              {i > 0 && <span className="text-ink-faint dark:text-gray-600">·</span>}
              <span className={`inline-flex items-center gap-1 ${bit.tone ?? ''}`}>
                {bit.key === 'conflicts' && <AlertTriangle className="w-3 h-3" />}
                {bit.text}
              </span>
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[minmax(140px,180px)_repeat(7,minmax(140px,1fr))] min-w-0">
        <div className="bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700" />
        {days.map((date) => (
          <div
            key={date.toISOString()}
            className={`
              p-2 sm:p-3 text-center border-r border-gray-200 dark:border-gray-700
              ${isToday(date)
                ? 'bg-blue-50 dark:bg-blue-900/20'
                : 'bg-gray-50 dark:bg-gray-800'}
            `}
          >
            <div className="text-[11px] font-medium text-ink-muted dark:text-text-light uppercase tracking-wide">
              {date.toLocaleDateString(locale, { weekday: 'short' })}
            </div>
            <div
              className={`
                text-lg font-semibold mt-0.5 text-tabular
                ${isToday(date)
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-900 dark:text-white'}
              `}
            >
              {String(date.getDate()).padStart(2, '0')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
